import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import * as CH from '@/components/CareerHistory/Form/Confirmation';
import { useMBXMediaQuery } from '@/hooks/useMBXMediaQuery';
import { FormLayoutUnitDivider, FormLabel } from '@/components/common/Form/';
import { BaseButton } from '@/components/common/Button/';
import { Project } from '@/utils/api-client';

export const ProjectsOriginal: React.VFC<{
  index: number;
  project?: Project[];
}> = ({ index, project }) => {
  const isFirstRun = useRef(true);

  useEffect(() => {
    // 初回スキップ
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
  }, [index]);

  return (
    <div className="mb-8 mbx-utils-stack-v--16">
      <div>
        {project &&
          project.map((item: Project, index) => (
            <div key={index} className="mbx-formUnit-education-box">
              <ProjectItem project={item} />
            </div>
          ))}
      </div>
    </div>
  );
};

const placeholder = {
  structure: `例）
・プロデューサー1名
・テクニカルディレクター1名
・アシスタントディレクター1名
・UI/UXデザイナー1名（本人）
・Webデザイナー1名
・フロントエンドエンジニア2名
・システムエンジニア1名`,
  tools: `例）
・Adobe XD
・Sketch
・Figma
・Photoshop
・Illustrator
・HTML
・CSS
・JavaScript
・PHP`,
};

export const ProjectItem: React.VFC<{
  project: Project;
}> = ({ project }) => {
  const mq = useMBXMediaQuery();
  const [open, setOpen] = useState<boolean>(true);

  return (
    <CH.DraggableSection className="" title="業務内容" nested={true} sortable={false}>
      <>
        <div className="space-y-8 lg:mb-22 md:mb-22 sm:mb-16">
          <FormLabel label="概要" type="small" />
          <div className="mkt_mask_items pointer-events-none opacity-40">
            <div className="mbx-formContainer--textfield-wrapper">
              <input
                type="text"
                className="mbx-formContainer mbx-formContainer--textfield"
                placeholder="例）アプリ開発におけるUI/UX設計およびパーツデザイン"
                value={project.name}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className={cn({ hidden: !open })}>
          <div className="space-y-8 mb-22">
            <FormLabel label="期間" type="small" />
            <div className="lg:flex md:flex lg:space-x-8 md:space-x-8 sm:space-y-8">
              <div className="flex space-x-8">
                <div className="w-80 pointer-events-none opacity-40">
                  <div className="mbx-formContainer--textfield-wrapper">
                    <input
                      type="number"
                      className="mbx-formContainer mbx-formContainer--textfield"
                      placeholder="yyyy"
                      value={project.start_date ? project.start_date.split('-')[0] : ''}
                      readOnly
                    />
                  </div>
                </div>
                <FormLayoutUnitDivider>年</FormLayoutUnitDivider>
                <div className="w-80 pointer-events-none opacity-40">
                  <div className="mbx-formContainer--textfield-wrapper">
                    <input
                      type="number"
                      className="mbx-formContainer mbx-formContainer--textfield"
                      placeholder="mm"
                      value={project.start_date ? project.start_date.split('-')[1] : ''}
                      readOnly
                    />
                  </div>
                </div>
                <FormLayoutUnitDivider>月 〜</FormLayoutUnitDivider>
              </div>
              <div className="flex space-x-8">
                <div className="w-80 pointer-events-none opacity-40">
                  <div className="mbx-formContainer--textfield-wrapper">
                    <input
                      type="number"
                      className="mbx-formContainer mbx-formContainer--textfield"
                      placeholder="yyyy"
                      value={project.end_date ? project.end_date.split('-')[0] : ''}
                      readOnly
                    />
                  </div>
                </div>
                <FormLayoutUnitDivider>年</FormLayoutUnitDivider>
                <div className="w-80 pointer-events-none opacity-40">
                  <div className="mbx-formContainer--textfield-wrapper">
                    <input
                      type="number"
                      className="mbx-formContainer mbx-formContainer--textfield"
                      placeholder="mm"
                      value={project.end_date ? project.end_date.split('-')[1] : ''}
                      readOnly
                    />
                  </div>
                </div>
                <FormLayoutUnitDivider>月</FormLayoutUnitDivider>
              </div>
            </div>
          </div>
          <div className="space-y-8 mb-12">
            <FormLabel label="詳細" type="small" />
            <div className="mkt_mask_items pointer-events-none opacity-40">
              <div className="mbx-formContainer mbx-formContainer--textarea">
                {project.detail ? (
                  project.detail
                ) : (
                  <p className="opacity-50">
                    例）某転職サイトの求人検索機能のアプリ化案件のUI/UXを担当。プロデューサー、ディレクターと共に競合アプリの分析を行い、アプリ全体のデザイン設計から、各パーツのデザインレギュレーション作成まで行った。求人検索自体の操作性だけでなく、ユーザーが応募した後に発生する人的運用や応募管理も総合的に考慮した最適なUX設計を実現することができた。
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-8 mb-12">
            <FormLabel label="役割・チーム体制" type="small" />
            <div className="mkt_mask_items pointer-events-none opacity-40">
              <div className="mbx-formContainer mbx-formContainer--textarea">
                {project.structure ? (
                  project.structure
                ) : (
                  <p className="opacity-50 whitespace-pre-wrap">{placeholder.structure}</p>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-8 mb-12">
            <FormLabel label="使用言語・ツール" type="small" />
            <div className="mkt_mask_items pointer-events-none opacity-40">
              <div className="mbx-formContainer mbx-formContainer--textarea">
                {project.tools ? (
                  project.tools
                ) : (
                  <p className="opacity-50 whitespace-pre-wrap">{placeholder.tools}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <BaseButton
          iconName="Arrow_Down"
          size="m"
          theme="quaternary"
          className={cn('toggle-button', { open, ['w-full']: mq.sm })}
          onClick={() => setOpen(!open)}
          type="button"
        >
          {open ? '業務内容を閉じる' : '詳細を確認する'}
        </BaseButton>
      </>
    </CH.DraggableSection>
  );
};
